/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * One appointment, drawn the same way wherever the phone shows one.
 *
 * The day screen and the list used to draw their own. They disagreed about
 * nearly everything a reader notices — one gave the start and the end, the
 * other the start and a duration; one put a colour bar beside the times, the
 * other an accent down the card's edge; the type differed in size and weight
 * — so switching between the two views read as switching between two
 * products. There is one row now, and the views differ only in how they
 * group it.
 *
 * The times sit in their own fixed column so the titles start on one line
 * down the list: a ragged left edge is what makes a list of times read as
 * unsorted even when it is in order.
 */

import dayjs from 'dayjs';
import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';

interface AgendaEventRowProps {
  event: ExpandedEvent;
  /** The event's colour, resolved by the caller from its calendar. */
  color: string;
  /** Second line: where it is, or which calendar it belongs to. */
  meta?: string;
  onClick: () => void;
}

export const AgendaEventRow: React.FC<AgendaEventRowProps> = ({
  event, color, meta, onClick,
}) => (
  <UnstyledButton
    data-testid="agenda-day-event"
    onClick={onClick}
    className={cn(
      'flex w-full items-stretch gap-(--space-3) rounded-(--radius-md) border border-[var(--border-subtle)]',
      'bg-[var(--surface-primary)] p-(--space-3) text-left',
    )}
    style={{ minHeight: 56 }}
  >
    <Text component="span" className="flex w-12 shrink-0 flex-col justify-center gap-px tabular-nums">
      <Text
        component="span"
        className="text-(length:--text-body-sm) leading-none text-[var(--text-primary)]"
        style={{ fontWeight: 'var(--font-semibold)' as unknown as number }}
      >
        {dayjs(event.start_at_utc).format('HH:mm')}
      </Text>
      {/*
        The end time is quieter than the start but still has to be read: at
        9px it was smaller than anything else in the product and effectively
        decorative.
      */}
      <Text component="span" className="text-(length:--text-meta) leading-none text-[var(--text-tertiary)]">
        {dayjs(event.end_at_utc).format('HH:mm')}
      </Text>
    </Text>

    <Text
      component="span"
      aria-hidden="true"
      className="w-[3px] shrink-0 rounded-full"
      style={{ background: color }}
    />

    <Text component="span" className="flex min-w-0 flex-1 flex-col justify-center gap-px">
      <Text
        component="span"
        className="truncate text-(length:--text-body-sm) text-[var(--text-primary)]"
        style={{ fontWeight: 'var(--font-semibold)' as unknown as number }}
      >
        {event.title}
      </Text>
      {meta && (
        <Text component="span" className="truncate text-(length:--text-meta) text-[var(--text-tertiary)]">
          {meta}
        </Text>
      )}
    </Text>
  </UnstyledButton>
);

export default AgendaEventRow;
